import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useRef } from 'react'
import GlobalLogger from '@shared/utils/logger/GlobalLogger'
import { isTurnLive, useReadAllChatMessages } from '../store'
import { describeTurnError, type ChatTurnError } from '../engine/errors'
import { resumeTurn } from '../engine/resume'
import { traceTurnEvent } from '../engine/telemetry'
import { describeCause } from '../engine/turn-trace'
import { startTurn, type TurnHandle } from '../engine/turn-registry'
import { useClearTurnError, useSetTurnError } from '../store/turn-error'
import { useAddChatToast } from '../store/toasts'
import { getResumeCursor, setResumeCursor } from './resume-cursors'
import { seedFromMessages } from './resume-seed'
import { useEngineDeps } from './use-engine-deps'
import { messagesQueryKey } from './messages-query'

type ActiveSubscription = {
  readonly chatId: string
  readonly handle: TurnHandle
}

export function useSubscribeToChat() {
  const deps = useEngineDeps()
  const setTurnError = useSetTurnError()
  const clearTurnError = useClearTurnError()
  const addChatToast = useAddChatToast()
  const activeRef = useRef<ActiveSubscription | null>(null)
  const queryClient = useQueryClient()

  /**
   * Let go of the stream this hook is following. Aborting a turn that is still
   * live leaves the backend generating with nobody folding its events into the
   * atom, so what we hold is missing everything from here on — invalidate the
   * chat's history so the next surface to open it re-pulls instead of
   * rendering the stream frozen where we let go. `refetchType: 'none'` because
   * the turn is still running: a snapshot taken now would be just as
   * incomplete, and fetching one would clear the invalidation we just set.
   */
  const release = useCallback(() => {
    const active = activeRef.current
    if (!active) return
    activeRef.current = null
    const turnLive = isTurnLive(active.chatId)
    if (turnLive) {
      void queryClient.invalidateQueries({ queryKey: messagesQueryKey(active.chatId), refetchType: 'none' })
    }
    // Module tap, not `deps.telemetry`: `release` is the consumer's effect
    // cleanup, and a `deps` dependency would abort the live stream whenever
    // the chat api rebuilds on a flag change.
    traceTurnEvent({ kind: 'subscribe:released', chatId: active.chatId, turnLive })
    active.handle.controller.abort('unsubscribed')
  }, [queryClient])

  // Read the atom on demand so the engine's lazy per-message seed sees current
  // content — a history refetch landing after connect still seeds. Deliberately
  // a read, not a subscription: this hook hosts the stream, so subscribing
  // would re-render it per delta.
  const readAllMessages = useReadAllChatMessages()

  const subscribe = useCallback(
    (chatId: string) => {
      // One subscription per hook instance at a time: the same chat is a
      // no-op, but a chat switch releases the previous stream (a client
      // abort — the backend turn keeps running and stays resumable) so the
      // new chat actually opens `/subscribe`. The turn registry additionally
      // guarantees a single owner per chat across instances.
      if (activeRef.current?.chatId === chatId) return
      release()

      // Reconnecting supersedes the chat's previous failure.
      clearTurnError(chatId)

      // Resume failures surface through the chat toast — the resume path has
      // no message-level error UI to stamp. The engine latches the first
      // failure, so this fires at most once per turn.
      const onTurnError = (turnError: ChatTurnError) => {
        setTurnError(turnError)
        addChatToast({ type: 'error', title: 'Chat error', message: describeTurnError(turnError) })
      }

      // One read feeds both the replay start and the seed decision. Reading
      // the cursor twice could pair a fresh cursor with a stale seed choice,
      // which is what doubles the replayed text.
      const cursor = getResumeCursor(chatId)
      const turnId = crypto.randomUUID()
      const handle = startTurn(chatId, (signal, isOwner) =>
        resumeTurn(
          deps,
          {
            chatId,
            turnId,
            seedFor: (messageId) => seedFromMessages(readAllMessages()[chatId], messageId, cursor !== undefined),
            onTurnError,
            isOwner,
            reconcileMessages: async () => {
              await queryClient.invalidateQueries({ queryKey: messagesQueryKey(chatId) })
            },
            lastEventId: cursor,
            onLastEventId: (lastEventId) => setResumeCursor(chatId, lastEventId),
          },
          signal,
        ),
      )
      activeRef.current = { chatId, handle }

      void handle.done
        .catch((cause: unknown) => {
          // Engine turns resolve Results and never reject; log if that
          // invariant breaks so the cleanup below still runs.
          GlobalLogger.error(`resume turn rejected unexpectedly chat=${chatId}: ${describeCause(cause)}`, {
            class: 'chat-turn',
          })
        })
        .then(() => {
          if (activeRef.current?.handle === handle) {
            activeRef.current = null
          }
        })
    },
    [deps, release, clearTurnError, setTurnError, addChatToast, readAllMessages],
  )

  return { subscribe, unsubscribe: release }
}
